import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileSignature, CheckCircle, AlertTriangle, Loader2, Phone,
  Calendar, Building2, User, MapPin, Shield, Clock, RefreshCw,
  PenTool, Type, X, ChevronDown, ChevronUp, FileText, Lock
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ContractSignPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('loading'); // loading, view, sms, sign, success, already_signed, expired

  // SMS state
  const [smsCode, setSmsCode] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [simulatedCode, setSimulatedCode] = useState('');

  // Signature state
  const [signatureType, setSignatureType] = useState('draw'); // draw, type
  const [signatureText, setSignatureText] = useState('');
  const [signatoryFirstName, setSignatoryFirstName] = useState('');
  const [signatoryLastName, setSignatoryLastName] = useState('');
  const [signatoryEmail, setSignatoryEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showContract, setShowContract] = useState(true);
  const [signatureResult, setSignatureResult] = useState(null); // Store signature result for success page

  // Canvas for drawing signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [token]);

  const fetchContract = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/v1/contracts/sign/${token}`);

      if (response.data.already_signed) {
        setStep('already_signed');
        setContract(response.data);
      } else {
        setContract(response.data);
        setSmsVerified(response.data.sms_validated);
        setStep(response.data.sms_validated ? 'sign' : 'view');
      }
    } catch (err) {
      console.error('Error fetching contract:', err);
      if (err.response?.status === 410) {
        setStep('expired');
        setError('Ce contrat a expiré');
      } else if (err.response?.status === 404) {
        setError('Contrat non trouvé ou lien invalide');
      } else {
        setError('Erreur lors du chargement du contrat');
      }
    } finally {
      setLoading(false);
    }
  };

  const requestSmsCode = async () => {
    try {
      setSmsLoading(true);
      const response = await axios.post(`${API_BASE}/api/v1/contracts/sign/${token}/request-sms`);

      setSmsSent(true);
      setStep('sms');

      // In simulation mode, show the code
      if (response.data.code) {
        setSimulatedCode(response.data.code);
        toast.info(`Code de simulation: ${response.data.code}`);
      }

      toast.success('Code SMS envoyé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur d\'envoi du code SMS');
    } finally {
      setSmsLoading(false);
    }
  };

  const verifySmsCode = async () => {
    if (smsCode.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }

    try {
      setSmsLoading(true);
      const response = await axios.post(`${API_BASE}/api/v1/contracts/sign/${token}/verify-sms`, {
        code: smsCode
      });

      if (response.data.success) {
        setSmsVerified(true);
        setStep('sign');
        toast.success('Code verifie avec succes');
      } else {
        toast.error(response.data.error || 'Code invalide');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de verification');
    } finally {
      setSmsLoading(false);
    }
  };

  const signContract = async () => {
    // Validate signatory info
    if (!signatoryFirstName.trim() || !signatoryLastName.trim()) {
      toast.error('Veuillez saisir votre prénom et nom');
      return;
    }

    if (!acceptTerms) {
      toast.error('Veuillez accepter les termes du contrat');
      return;
    }

    let signatureData = null;

    if (signatureType === 'draw') {
      if (!hasDrawn) {
        toast.error('Veuillez dessiner votre signature');
        return;
      }
      signatureData = canvasRef.current?.toDataURL('image/png');
    } else {
      if (!signatureText.trim()) {
        toast.error('Veuillez saisir votre nom pour la signature');
        return;
      }
    }

    const fullName = `${signatoryFirstName.trim()} ${signatoryLastName.trim()}`;

    try {
      setSigning(true);
      const response = await axios.post(`${API_BASE}/api/v1/contracts/sign/${token}/sign`, {
        type: signatureType,
        data: signatureData,
        text: signatureText || fullName,
        signatory_first_name: signatoryFirstName.trim(),
        signatory_last_name: signatoryLastName.trim(),
        signatory_email: signatoryEmail.trim() || null,
        legal_mention: "Bon pour acceptation de Pentest"
      });

      if (response.data.success) {
        setSignatureResult(response.data);
        setStep('success');
        toast.success('Contrat signe avec succes!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  // Canvas drawing functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (step === 'sign' && signatureType === 'draw') {
      initCanvas();
    }
  }, [step, signatureType]);

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    // Scale coordinates to match canvas internal resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    initCanvas();
    setHasDrawn(false);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement du contrat...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !contract) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-8 max-w-md w-full text-center border border-red-500/30">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Erreur</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2 mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Render expired state
  if (step === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-8 max-w-md w-full text-center border border-yellow-500/30">
          <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Contrat expiré</h1>
          <p className="text-gray-400">Ce lien de signature a expiré. Veuillez contacter le prestataire pour obtenir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  // Render already signed state
  if (step === 'already_signed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-8 max-w-md w-full text-center border border-green-500/30">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Contrat déjà signé</h1>
          <p className="text-gray-400 mb-4">
            Ce contrat a déjà été signé le {contract?.signed_at ? new Date(contract.signed_at).toLocaleDateString('fr-FR') : 'N/A'}.
          </p>
          <p className="text-sm text-gray-500">Référence : {contract?.contract_number}</p>
        </div>
      </div>
    );
  }

  // Render success state with DocuSign-like signature metadata
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-8 max-w-lg w-full border border-green-500/30">
          {/* Success Header */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Contrat signé avec succès !</h1>
            <p className="text-gray-400">
              Merci d'avoir signé le contrat. Vous recevrez une copie par e-mail.
            </p>
          </div>

          {/* Signature Certificate Box (DocuSign-style) */}
          <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden mb-6">
            <div className="bg-green-500/10 border-b border-gray-700 px-4 py-3">
              <h3 className="text-green-400 font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Certificat de signature électronique
              </h3>
            </div>

            <div className="p-4 space-y-4">
              {/* Contract Reference */}
              <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                <span className="text-gray-400 text-sm">Référence du contrat</span>
                <span className="text-white font-mono">{contract?.contract_number || signatureResult?.contract_number}</span>
              </div>

              {/* Signatory Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-300">
                  <User className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">Signataire</span>
                </div>
                <div className="ml-6 space-y-1">
                  <p className="text-white font-medium">
                    {signatureResult?.signatory?.first_name} {signatureResult?.signatory?.last_name}
                  </p>
                  {signatureResult?.signatory?.email && (
                    <p className="text-gray-400 text-sm">{signatureResult?.signatory?.email}</p>
                  )}
                </div>
              </div>

              {/* Signature Time */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm">Date et heure de signature</span>
                </div>
                <div className="ml-6">
                  <p className="text-white">
                    {signatureResult?.signed_at ? new Date(signatureResult.signed_at).toLocaleString('fr-FR', {
                      dateStyle: 'full',
                      timeStyle: 'medium',
                      timeZone: 'UTC'
                    }) + ' (UTC)' : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Verification Info */}
              {signatureResult?.verification && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Lock className="w-4 h-4 text-green-400" />
                    <span className="text-sm">Vérification</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span className="text-gray-300 text-sm">Vérification SMS : <span className="text-green-400">Validée</span></span>
                    </div>
                    <p className="text-gray-500 text-xs">
                      Adresse IP : {signatureResult?.verification?.ip_address || 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* Document Integrity */}
              {signatureResult?.verification?.document_hash && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-300">
                    <FileText className="w-4 h-4 text-purple-400" />
                    <span className="text-sm">Intégrité du document</span>
                  </div>
                  <div className="ml-6">
                    <p className="text-gray-500 text-xs font-mono break-all">
                      SHA-512: {signatureResult?.verification?.document_hash}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-800/50 border-t border-gray-700 px-4 py-3">
              <p className="text-xs text-gray-500 text-center">
                Signature électronique conforme au règlement eIDAS (UE) 910/2014
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center">
            <p className="text-sm text-gray-500">
              Une copie de ce certificat sera envoyée par e-mail.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-full px-6 py-2 mb-4">
            <Shield className="w-5 h-5 text-green-500" />
            <span className="text-green-400 text-sm font-medium">Signature électronique sécurisée</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Signature de contrat</h1>
          <p className="text-gray-400">Contrat d'audit de sécurité - {contract?.client_name}</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {['Lecture', 'Vérification', 'Signature'].map((label, index) => {
            const stepNum = index + 1;
            const currentStepNum = step === 'view' ? 1 : step === 'sms' ? 2 : 3;
            const isActive = stepNum === currentStepNum;
            const isCompleted = stepNum < currentStepNum;

            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCompleted ? 'bg-green-500 text-white' :
                  isActive ? 'bg-green-500/20 text-green-400 border-2 border-green-500' :
                  'bg-gray-700 text-gray-500'
                }`}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : stepNum}
                </div>
                <span className={`text-sm ${isActive ? 'text-white' : 'text-gray-500'}`}>{label}</span>
                {index < 2 && <div className="w-12 h-px bg-gray-700 ml-2" />}
              </div>
            );
          })}
        </div>

        {/* Contract Card */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 overflow-hidden">
          {/* Contract Header */}
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">{contract?.title}</h2>
                <p className="text-sm text-gray-400 mt-1">Référence : {contract?.contract_number}</p>
              </div>
              <button
                onClick={() => setShowContract(!showContract)}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
              >
                {showContract ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Contract Content - All sections displayed vertically */}
          {showContract && (
            <div className="border-b border-gray-700">
              <div className="p-6 space-y-6">
                {/* Section 1: Informations */}
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700">
                  <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Informations
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Prestataire</h4>
                      <p className="text-white">{contract?.provider_name}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Client</h4>
                      <p className="text-white">{contract?.client_name}</p>
                      <p className="text-sm text-gray-500">{contract?.client_representative_name}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Date de début</h4>
                      <p className="text-white flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        {contract?.start_date ? new Date(contract.start_date).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Date de fin</h4>
                      <p className="text-white flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        {contract?.end_date ? new Date(contract.end_date).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Périmètre */}
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700">
                  <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Périmètre des tests
                  </h3>
                  {Array.isArray(contract?.perimeter) && contract.perimeter.length > 0 ? (
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="text-left pb-2">Réf.</th>
                            <th className="text-left pb-2">Type</th>
                            <th className="text-left pb-2">Détail</th>
                            <th className="text-left pb-2">Env.</th>
                            <th className="text-left pb-2">Commentaire</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          {contract.perimeter.map((item, idx) => (
                            <tr key={idx} className="border-t border-gray-700">
                              <td className="py-2 text-green-400 font-medium">{item.ref}</td>
                              <td className="py-2">
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                                  {item.type}
                                </span>
                              </td>
                              <td className="py-2 font-mono text-xs">{item.detail}</td>
                              <td className="py-2">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  item.env === 'Production' ? 'bg-red-500/20 text-red-300' :
                                  item.env === 'Test' ? 'bg-yellow-500/20 text-yellow-300' :
                                  'bg-gray-500/20 text-gray-300'
                                }`}>
                                  {item.env}
                                </span>
                              </td>
                              <td className="py-2 text-gray-400 text-xs">{item.comment || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Aucun périmètre défini</p>
                    </div>
                  )}
                </div>

                {/* Section 3: Contrat complet */}
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700">
                  <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Contrat complet
                  </h3>
                  <div className="bg-white rounded-lg p-6 text-gray-900 text-sm leading-relaxed">
                    <div className="text-center border-b-2 border-gray-300 pb-4 mb-6">
                      <h1 className="text-lg font-bold mb-1">CONTRAT D'AUTORISATION DE TEST D'INTRUSION ET D'ATTAQUE SUR RÉSEAU INFORMATIQUE</h1>
                      <p className="text-xs text-gray-500">(avec information et acceptation des risques)</p>
                      <p className="text-xs text-gray-600 mt-2">Référence : {contract?.contract_number}</p>
                    </div>

                    <div className="space-y-4 text-xs">
                      <div>
                        <p className="mb-2">Entre les soussignés :</p>
                        <div className="bg-gray-50 p-3 rounded mb-2">
                          <p><strong>{contract?.client_name || '[Nom du Client]'}</strong>,</p>
                          <p>{contract?.client_legal_form || '[forme juridique]'}, au capital de {contract?.client_capital || '[X]'} EUR,</p>
                          <p>dont le siège social est situé {contract?.client_address || '[adresse complète]'}, {contract?.client_postal_code} {contract?.client_city},</p>
                          {contract?.client_siret && <p>SIRET : {contract.client_siret},</p>}
                          <p>représentée par <strong>{contract?.client_representative_name || '[Nom, fonction]'}</strong>, {contract?.client_representative_function || 'dûment habilité'},</p>
                          <p className="mt-1">Ci-après dénommée <strong>« le Client »</strong>,</p>
                        </div>
                        <p className="text-center font-bold my-2">D'UNE PART,</p>
                        <p className="text-center my-2">ET</p>
                        <div className="bg-gray-50 p-3 rounded mb-2">
                          <p><strong>{contract?.provider_name || '[Nom du Prestataire]'}</strong>,</p>
                          <p>{contract?.provider_legal_form || '[forme juridique]'}, au capital de {contract?.provider_capital || '[X]'} EUR,</p>
                          <p>dont le siège social est situé {contract?.provider_address || '[adresse complète]'}, {contract?.provider_postal_code} {contract?.provider_city},</p>
                          {contract?.provider_siret && <p>SIRET : {contract.provider_siret},</p>}
                          <p>représentée par <strong>{contract?.provider_representative_name || '[Nom, fonction]'}</strong>, {contract?.provider_representative_function || 'dûment habilité'},</p>
                          <p className="mt-1">Ci-après dénommée <strong>« le Prestataire »</strong>,</p>
                        </div>
                        <p className="text-center font-bold my-2">D'AUTRE PART,</p>
                        <p className="text-center text-gray-600">Ensemble dénommés « les Parties » ou individuellement une « Partie ».</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">1. OBJET DU CONTRAT</h2>
                        <p>Le présent contrat a pour objet de définir les conditions dans lesquelles le Prestataire est autorisé par le Client à réaliser des tests d'intrusion, simulations d'attaques et autres actions offensives de cybersécurité (ci-après les "Tests") sur les systèmes d'information du Client, dans le but d'identifier des failles de sécurité et de formuler des recommandations.</p>
                        <p className="mt-2">Les Tests peuvent inclure, selon ce qui est défini dans ce contrat et ses annexes :</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          <li>Tests boite noire, boite grise et/ou boite blanche ;</li>
                          <li>Tests sur réseau interne, réseau externe, applications Web, services exposés, systèmes industriels/OT, équipements réseau, Wi-Fi, etc. ;</li>
                          <li>Simulations d'attaques techniques (exploitation de vulnérabilités, élévation de privilèges, mouvements latéraux, etc.) dans les limites définies au présent contrat.</li>
                        </ul>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">2. CHAMP D'APPLICATION ET PÉRIMÈTRE</h2>
                        <p>Les Tests sont strictement limités au périmètre suivant, fourni par le Client et accepté par le Prestataire :</p>
                        {Array.isArray(contract?.perimeter) && contract.perimeter.length > 0 ? (
                          <table className="w-full border border-gray-300 mt-2 mb-2">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="border border-gray-300 px-2 py-1 text-left">Réf.</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Type</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Détail</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Environnement</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contract.perimeter.map((item, idx) => (
                                <tr key={idx}>
                                  <td className="border border-gray-300 px-2 py-1">{item.ref}</td>
                                  <td className="border border-gray-300 px-2 py-1">{item.type}</td>
                                  <td className="border border-gray-300 px-2 py-1 font-mono">{item.detail}</td>
                                  <td className="border border-gray-300 px-2 py-1">{item.env}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-gray-500 italic my-2">[Périmètre à définir]</p>
                        )}
                        <p className="mt-2"><strong>Tout système non explicitement mentionné dans ce périmètre est exclu des Tests.</strong></p>
                        <p>Toute extension de périmètre devra faire l'objet d'un avenant écrit accepté par les deux Parties.</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">3. PÉRIODE ET CRÉNEAUX D'INTERVENTION</h2>
                        <p>Les Tests seront réalisés entre les dates suivantes :</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          <li>Date de début des Tests : <strong>{contract?.start_date ? new Date(contract.start_date).toLocaleDateString('fr-FR') : '[JJ/MM/AAAA]'}</strong></li>
                          <li>Date de fin des Tests : <strong>{contract?.end_date ? new Date(contract.end_date).toLocaleDateString('fr-FR') : '[JJ/MM/AAAA]'}</strong></li>
                        </ul>
                        <p className="mt-2">Créneaux autorisés : {contract?.time_slots || 'Du lundi au vendredi, de 9h00 à 18h00'}</p>
                        <p className="mt-1">Toute modification de ces créneaux devra être validée par écrit par les deux Parties.</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">4. INFORMATION SUR LES RISQUES ET ACCEPTATION</h2>
                        <p>Le Client reconnaît avoir été informé par le Prestataire que, malgré toutes les précautions prises, les Tests peuvent entraîner notamment :</p>
                        <ol className="list-decimal list-inside ml-2 mt-1">
                          <li>Ralentissements, dégradations de performance ou indisponibilités temporaires de certains services ;</li>
                          <li>Blocages d'applications ou de sessions utilisateurs ;</li>
                          <li>Faux positifs de sécurité (déclenchements d'alertes sur des outils de supervision / sécurité) ;</li>
                          <li>Corruption ou perte de données en cas de combinatoire imprévue de vulnérabilités ;</li>
                          <li>Risques accrus sur des systèmes sensibles (infrastructures industrielles/OT, équipements réseau critiques, systèmes anciens/obsolètes).</li>
                        </ol>
                        <p className="mt-2"><strong>Le Prestataire s'engage à :</strong></p>
                        <ul className="list-disc list-inside ml-2">
                          <li>Mettre en œuvre les bonnes pratiques du métier pour limiter les risques ;</li>
                          <li>Arrêter immédiatement un test en cas de dysfonctionnement grave constaté ou signalé ;</li>
                          <li>Informer le Client dès la découverte d'un risque sérieux.</li>
                        </ul>
                        <p className="mt-2"><strong>Le Client :</strong></p>
                        <ul className="list-disc list-inside ml-2">
                          <li>Accepte explicitement ces risques liés à la réalisation de Tests d'intrusion ;</li>
                          <li>Reconnaît être responsable de la mise en place de sauvegardes récentes et vérifiées avant les Tests ;</li>
                          <li>S'engage à désigner un contact technique joignable pendant toute la durée des Tests.</li>
                        </ul>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">5. AUTORISATION EXPRESSE DU CLIENT</h2>
                        <p>Le Client autorise expressément le Prestataire à :</p>
                        <ol className="list-decimal list-inside ml-2 mt-1">
                          <li>Réaliser des Tests d'intrusion et simulations d'attaque sur les systèmes définis au périmètre ;</li>
                          <li>Se connecter, scanner, analyser, tenter d'exploiter des vulnérabilités et évaluer le niveau de sécurité ;</li>
                          <li>Utiliser des outils de pentest, d'exploitation, d'analyse de trafic, de fuzzing, etc.</li>
                        </ol>
                        <p className="mt-2">Cette autorisation est limitée dans le temps, le périmètre et le but (exclusivement audit de sécurité).</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">6. CONFIDENTIALITÉ ET TRAITEMENT DES DONNÉES</h2>
                        <p><strong>Le Prestataire s'engage à :</strong></p>
                        <ul className="list-disc list-inside ml-2">
                          <li>Traiter comme strictement confidentielles toutes les informations et vulnérabilités découvertes ;</li>
                          <li>Utiliser les données collectées uniquement pour la réalisation de la mission ;</li>
                          <li>Ne pas transmettre à des tiers les informations sans l'accord écrit du Client ;</li>
                          <li>Mettre en place des mesures techniques et organisationnelles adaptées (chiffrement, contrôle d'accès, etc.).</li>
                        </ul>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">7. RESPONSABILITÉS</h2>
                        <p><strong>Le Prestataire :</strong></p>
                        <ul className="list-disc list-inside ml-2">
                          <li>Est tenu à une obligation de moyens et non de résultat ;</li>
                          <li>S'engage à exécuter les Tests conformément aux règles de l'art et standards (OWASP, guides ANSSI, etc.) ;</li>
                          <li>Ne pourra être tenu responsable de dommages résultant de l'absence de sauvegardes ou dysfonctionnements préexistants.</li>
                        </ul>
                        <p className="mt-2"><strong>Le Client :</strong></p>
                        <ul className="list-disc list-inside ml-2">
                          <li>Est responsable de la validité de l'autorisation qu'il donne ;</li>
                          <li>S'engage à fournir des informations exactes sur l'infrastructure ;</li>
                          <li>Est responsable des décisions de correction suite aux recommandations.</li>
                        </ul>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">8. LIVRABLES</h2>
                        <p>À l'issue des Tests, le Prestataire fournira au Client :</p>
                        <ol className="list-decimal list-inside ml-2 mt-1">
                          <li>Un rapport technique détaillé (périmètre, méthodologie, vulnérabilités, preuves de concept, vecteurs d'attaque) ;</li>
                          <li>Un rapport de synthèse à destination de la Direction (principaux risques, priorisation, recommandations stratégiques).</li>
                        </ol>
                        <p className="mt-2">{contract?.deliverables_description || "Les livrables sont remis sous format PDF."}</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">9. DURÉE DU CONTRAT</h2>
                        <p>Le présent contrat entre en vigueur à compter de sa date de signature et reste valable jusqu'à la remise des livrables finaux ou la date de fin des Tests, selon la première échéance.</p>
                        <p className="mt-1">Toute nouvelle campagne de Tests nécessitera la signature d'un nouveau contrat ou avenant.</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">10. DROIT APPLICABLE ET JURIDICTION</h2>
                        <p>Le présent contrat est régi par le droit {contract?.applicable_law || 'français'}.</p>
                        <p>En cas de litige, les tribunaux de {contract?.jurisdiction || 'Paris'} seront seuls compétents.</p>
                      </div>

                      <hr className="my-4" />

                      <div>
                        <h2 className="font-bold text-sm mb-2">11. SIGNATURES ET CLAUSE D'ACCEPTATION DES RISQUES</h2>
                        <p>En signant le présent contrat, le Client :</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          <li>Reconnaît avoir été informé de la nature offensive des Tests ;</li>
                          <li>Comprend et accepte les risques inhérents aux Tests décrits à l'article 4 ;</li>
                          <li>Autorise expressément le Prestataire à réaliser les Tests conformément aux termes du présent contrat.</li>
                        </ul>
                        <p className="mt-3 text-center text-gray-600">Fait à _____________, le {new Date().toLocaleDateString('fr-FR')}, en deux exemplaires originaux.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Section */}
          <div className="p-6">
            {/* Step 1: View & Request SMS */}
            {step === 'view' && (
              <div className="text-center">
                <div className="mb-6">
                  <Lock className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">Vérification par SMS requise</h3>
                  <p className="text-gray-400 text-sm">
                    Pour signer ce contrat, vous devez vérifier votre identité via un code SMS.
                  </p>
                </div>
                <button
                  onClick={requestSmsCode}
                  disabled={smsLoading}
                  className="px-8 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {smsLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Phone className="w-5 h-5" />
                  )}
                  Recevoir le code SMS
                </button>
              </div>
            )}

            {/* Step 2: SMS Verification */}
            {step === 'sms' && (
              <div className="max-w-sm mx-auto text-center">
                <Phone className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Entrez le code SMS</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Un code à 6 chiffres a été envoyé à votre téléphone.
                </p>

                {simulatedCode && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm">Mode simulation - Code: <span className="font-mono font-bold">{simulatedCode}</span></p>
                  </div>
                )}

                <input
                  type="text"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full text-center text-3xl font-mono tracking-widest px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-green-500 mb-4"
                  maxLength={6}
                />

                <div className="flex gap-3">
                  <button
                    onClick={requestSmsCode}
                    disabled={smsLoading}
                    className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
                  >
                    Renvoyer
                  </button>
                  <button
                    onClick={verifySmsCode}
                    disabled={smsLoading || smsCode.length !== 6}
                    className="flex-1 px-4 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {smsLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Vérifier'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Signature */}
            {step === 'sign' && (
              <div>
                {/* Signatory Identity */}
                <div className="mb-6 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-green-500" />
                    Identité du signataire
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Prénom *</label>
                      <input
                        type="text"
                        value={signatoryFirstName}
                        onChange={(e) => setSignatoryFirstName(e.target.value)}
                        placeholder="Votre prénom"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Nom *</label>
                      <input
                        type="text"
                        value={signatoryLastName}
                        onChange={(e) => setSignatoryLastName(e.target.value)}
                        placeholder="Votre nom"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">E-mail (optionnel)</label>
                    <input
                      type="email"
                      value={signatoryEmail}
                      onChange={(e) => setSignatoryEmail(e.target.value)}
                      placeholder="email@exemple.fr"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Une copie du contrat signé sera envoyée à cette adresse</p>
                  </div>
                </div>

                {/* Signature Type Toggle */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <button
                    onClick={() => setSignatureType('draw')}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      signatureType === 'draw'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <PenTool className="w-4 h-4" />
                    Dessiner
                  </button>
                  <button
                    onClick={() => setSignatureType('type')}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      signatureType === 'type'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <Type className="w-4 h-4" />
                    Saisir
                  </button>
                </div>

                {/* Draw Signature */}
                {signatureType === 'draw' ? (
                  <div className="mb-6">
                    <label className="block text-sm text-gray-400 mb-2">Dessinez votre signature</label>
                    <div className="relative bg-white rounded-lg overflow-hidden">
                      <canvas
                        ref={canvasRef}
                        width={500}
                        height={150}
                        className="w-full cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                      <button
                        onClick={clearCanvas}
                        className="absolute top-2 right-2 p-2 bg-gray-800/80 text-white rounded-lg hover:bg-gray-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">Dessinez votre signature dans le cadre blanc</p>
                  </div>
                ) : (
                  <div className="mb-6">
                    <label className="block text-sm text-gray-400 mb-2">Saisissez votre signature</label>
                    <input
                      type="text"
                      value={signatureText}
                      onChange={(e) => setSignatureText(e.target.value)}
                      placeholder="Votre nom complet"
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500 text-lg"
                    />
                    {signatureText && (
                      <div className="mt-4 p-4 bg-white rounded-lg">
                        <p className="text-2xl text-gray-800 font-signature text-center" style={{ fontFamily: 'cursive' }}>
                          {signatureText}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Legal Mention Box */}
                <div className="mb-6 p-4 bg-blue-900/30 border border-blue-500/30 rounded-lg">
                  <h4 className="text-blue-300 font-medium mb-2">Mention légale</h4>
                  <p className="text-white text-lg font-medium text-center py-2 bg-white/10 rounded">
                    "Bon pour acceptation de Pentest"
                  </p>
                  <p className="text-blue-200 text-xs mt-2">
                    En signant ce contrat, vous certifiez avoir pris connaissance du périmètre des tests et autorisez le prestataire à effectuer les tests d'intrusion sur les systèmes définis.
                  </p>
                </div>

                {/* Accept Terms Checkbox */}
                <div className="flex items-start gap-3 mb-6 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                  <input
                    type="checkbox"
                    id="accept"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="w-5 h-5 mt-0.5 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-500"
                  />
                  <label htmlFor="accept" className="text-sm text-gray-400">
                    Je, <span className="text-white font-medium">{signatoryFirstName} {signatoryLastName}</span>, certifie avoir lu et accepté les termes du contrat d'autorisation de test d'intrusion.
                    Je comprends que ma signature électronique a valeur légale et que j'autorise le prestataire à effectuer les tests de sécurité dans le périmètre défini.
                  </label>
                </div>

                {/* Sign Button */}
                <button
                  onClick={signContract}
                  disabled={signing || !acceptTerms || !signatoryFirstName || !signatoryLastName}
                  className="w-full px-6 py-4 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
                >
                  {signing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileSignature className="w-5 h-5" />
                  )}
                  Signer le contrat
                </button>

                <p className="text-center text-xs text-gray-500 mt-4">
                  Signature électronique sécurisée conforme au règlement eIDAS
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Signature électronique sécurisée par SYLink Pentest</p>
          <p className="mt-1">En cas de problème, contactez le prestataire.</p>
        </div>
      </div>
    </div>
  );
};

export default ContractSignPage;
